import axios from 'axios';
import type {
  WhatsAppButton,
  CarouselCard,
  SuggestedTemplate,
  SuggestTemplatesResponse,
  WhatsAppTemplateType,
  WhatsAppTemplateScope,
  WhatsAppTemplateStatus,
  WhatsAppTemplateCategory,
  WhatsAppButtonType,
  WhatsAppHeaderFormat,
} from '../types';
import type { Asset } from '../types/asset';

// Re-export types for consumers of this API
export type { WhatsAppButton, CarouselCard, SuggestedTemplate, SuggestTemplatesResponse };
export type { Asset };
export type {
  WhatsAppTemplateType,
  WhatsAppTemplateScope,
  WhatsAppTemplateStatus,
  WhatsAppTemplateCategory,
  WhatsAppButtonType,
  WhatsAppHeaderFormat,
};

// Local interface for API responses (standalone to avoid type-widening conflicts)
export interface WhatsAppTemplate {
  id: string;
  name: string;
  display_name: string;
  language?: string;
  category?: WhatsAppTemplateCategory;
  template_type: WhatsAppTemplateType | string;
  template_scope?: WhatsAppTemplateScope | string;
  header_format?: WhatsAppHeaderFormat;
  header_text?: string;
  header_text_variables?: string[];
  header_media_url?: string;
  header_media_handle?: string;
  body: string;
  body_variables?: string[];
  footer?: string;
  button_type?: string;
  buttons: WhatsAppButton[];
  interactive_buttons?: { id: string; title: string }[];
  carousel_cards: CarouselCard[];
  tags: string[];
  status: WhatsAppTemplateStatus | string;
  interakt_template_id?: string;
  wa_template_id?: string;
  rejection_reason?: string;
  created_at?: string;
  updated_at?: string;
  // Fields from compose-template API
  suggested_message?: string | null;
  has_buttons?: boolean;
  has_carousel?: boolean;
}

// Agent Service API Configuration (for intelligent template suggestions)
const AGENT_SERVICE_URL = 'https://agent-service.bhuvanafortunegreens.com';
const AGENT_SERVICE_API_KEY = 'b9e4k2k2m5n7p1q4r6s8t0u2v5w7x9y1';

const agentServiceClient = axios.create({
  baseURL: AGENT_SERVICE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': AGENT_SERVICE_API_KEY
  }
});

// Personal-WhatsApp share kit (agent-service) — the same kit and drafting the
// Nurture app's share sheet uses. Everything here goes out from the operator's
// OWN WhatsApp; nothing touches the business line.
export interface ShareAsset {
  id: string; label: string; kind: string; caption: string;
  supports_file: boolean; supports_link: boolean;
}
export interface ShareSuggestion { id: string; label: string; text: string }
export interface SharePlaybook { id: string; label: string; step_count: number }
export interface ShareCategory { id: string; name: string; items: { kind: 'asset' | 'suggestion' | 'playbook'; id: string }[] }
export interface ShareKit {
  assets: ShareAsset[]; suggestions: ShareSuggestion[]; playbooks: SharePlaybook[]; categories: ShareCategory[];
}
export interface ShareStep {
  kind: 'text' | 'attachment';
  asset_id?: string | null; text?: string | null;
  url?: string | null; mime?: string | null; filename?: string | null; caption?: string | null;
}

export const shareApi = {
  kit: async (): Promise<ShareKit> => (await agentServiceClient.get('/agent/share-assets')).data,
  draft: async (req: {
    lead_id: string; playbook?: string; operator_name?: string; operator_email?: string; hint?: string;
    // The picks — assets and ready-made suggestions — in send order.
    assets?: { id: string; kind?: 'asset' | 'suggestion'; mode?: 'file' | 'link' }[]; combine?: boolean; verbatim?: boolean;
  }): Promise<{ steps: ShareStep[] }> => (await agentServiceClient.post('/agent/draft-share', req)).data,
};

// Marketing Service API Configuration (fallback)
const MARKETING_API_URL = 'https://marketing-service.bhuvanafortunegreens.com';
const MARKETING_API_KEY = '4G6lRIEN4pq6V3zfGV+n7zSOwNe893UEGtg7z1Ij8qY=';

const marketingClient = axios.create({
  baseURL: MARKETING_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${MARKETING_API_KEY}`
  }
});

// Marketing API Methods
export const marketingApi = {
  // Compose templates based on conversation context
  composeTemplates: async (
    conversationId: string
  ): Promise<SuggestTemplatesResponse> => {
    try {
      const response = await agentServiceClient.post('/agent/compose-template', {
        conversation_id: conversationId
      });

      // Map the response to our WhatsAppTemplate format
      // Note: The suggest-templates API returns limited data (no media URLs)
      // For full template data with images, use getTemplate(templateName)
      const templates = (response.data.templates || []).map((t: any) => ({
        id: t.name,
        name: t.name,
        display_name: t.display_name,
        template_type: t.template_type || t.type,
        template_scope: 'IN_SESSION',
        body: t.body || t.description || '',
        tags: t.tags || [],
        status: t.status || 'APPROVED',
        suggested_message: t.suggested_message,
        has_buttons: t.has_buttons || false,
        has_carousel: t.has_carousel || false,
        header_format: t.header_format,
        header_text: t.header_text,
        header_media_url: t.header_media_url,
        footer: t.footer,
        button_type: t.button_type,
        buttons: t.buttons || [],
        interactive_buttons: t.interactive_buttons || [],
        carousel_cards: t.carousel_cards || []
      }));

      return {
        templates,
        reasoning: response.data.reasoning || '',
        error: response.data.error
      };
    } catch (error) {
      console.error('Error fetching suggested templates:', error);
      throw error;
    }
  },

  // Get WhatsApp templates filtered by scope and status (fallback)
  getTemplates: async (
    templateScope: string = 'IN_SESSION',
    status: string = 'APPROVED'
  ): Promise<WhatsAppTemplate[]> => {
    try {
      const response = await marketingClient.get('/api/v1/whatsapp-templates', {
        params: {
          template_scope: templateScope,
          status: status
        }
      });
      return response.data.templates || response.data;
    } catch (error) {
      console.error('Error fetching WhatsApp templates:', error);
      throw error;
    }
  },

  // Get a specific template by ID
  getTemplate: async (templateId: string): Promise<WhatsAppTemplate> => {
    try {
      const response = await marketingClient.get(`/api/v1/whatsapp-templates/${templateId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching WhatsApp template:', error);
      throw error;
    }
  },

  // Get assets with optional type filter
  getAssets: async (type?: 'image' | 'video' | 'pdf'): Promise<Asset[]> => {
    try {
      const params: Record<string, string> = {};
      if (type) {
        params.type = type;
      }
      const response = await marketingClient.get('/api/v1/assets', { params });
      // Handle both { assets: [...] } and direct array response
      return response.data.assets || response.data || [];
    } catch (error) {
      console.error('Error fetching assets:', error);
      throw error;
    }
  }
};

export default marketingApi;
